import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Button, DateField, Field, FieldError, FileDrop, Icon, Modal, Segmented } from "@otrack/ui";
import { useApp, useFactory } from "./context";
import { planSolidtimeImport, readSolidtimeExport, type SolidtimeExport } from "./import/solidtime";
import { startOfDay } from "./time";

type Scope = "all" | "range";

export function ImportDialog({ onClose }: { onClose: () => void }) {
  const { act } = useApp();
  const factory = useFactory();
  const [source, setSource] = useState<SolidtimeExport | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState<Scope>("all");
  const [from, setFrom] = useState(() => startOfDay(Date.now()));
  const [to, setTo] = useState(() => startOfDay(Date.now()));

  const read = async (file: File) => {
    setError(null);
    setSource(null);
    setFileName(file.name);
    setBusy(true);
    try {
      const next = await readSolidtimeExport(file);
      setSource(next);
      setFrom(startOfDay(next.span?.from ?? Date.now()));
      setTo(startOfDay(next.span?.to ?? Date.now()));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The file could not be read.");
    } finally {
      setBusy(false);
    }
  };

  // Days are inclusive, so the range runs to the start of the day after the later one.
  const result = useMemo(() => {
    if (!source) return null;
    const range = scope === "range" ? { from: Math.min(from, to), to: startOfDay(Math.max(from, to), 1) } : null;
    return planSolidtimeImport(source, factory, range);
  }, [source, factory, scope, from, to]);

  const confirm = async () => {
    if (!result) return;
    setBusy(true);
    await act(result.operations, `Imported ${result.counts.timeEntries} entries from solidtime`, { undoable: false });
    onClose();
  };

  return <Modal title="Import from solidtime" icon="upload" description="Bring your clients, projects, tasks, and time over with their rates." onClose={onClose}>
    <div className="ot-form">
      <ol className="ot-steps">
        <li><span>1</span>In solidtime, open your organization settings and export your data.</li>
        <li><span>2</span>Drop the <code className="ot-code">export.zip</code> here. Importing it again later updates instead of duplicating.</li>
      </ol>
      <FileDrop accept=".zip,application/zip" icon="file-up" title={result && fileName ? fileName : "Drop export.zip, or browse"} hint={result ? "Choose another file to replace it" : "The zip solidtime gives you"} busy={busy && !result} onFile={(file) => void read(file)}/>
      <FieldError>{error}</FieldError>
      <AnimatePresence>
        {result && <motion.div key="summary" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: "hidden" }}>
          <div className="ot-form">
            <Field label="Time entries" hint={scope === "range" ? "Only entries that start in this period come over. Clients, projects, and tasks always do." : undefined}>
              <Segmented stretch value={scope} onChange={setScope} options={[{ value: "all", label: "All time" }, { value: "range", label: "Date range" }]}/>
            </Field>
            <AnimatePresence initial={false}>
              {scope === "range" && <motion.div key="range" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: "hidden" }}>
                <div className="ot-form__pair">
                  <Field label="From"><DateField value={from} onChange={setFrom}/></Field>
                  <Field label="To"><DateField value={to} onChange={setTo}/></Field>
                </div>
              </motion.div>}
            </AnimatePresence>
            <div>
              <div className="ot-import__found"><Icon name="check" weight="bold"/>Ready to import from <strong>{result.organization}</strong> ({result.currency})</div>
              <dl className="ot-summary-grid">
                <div><dt>Clients</dt><dd className="ot-mono">{result.counts.clients}</dd></div>
                <div><dt>Projects</dt><dd className="ot-mono">{result.counts.projects}</dd></div>
                <div><dt>Tasks</dt><dd className="ot-mono">{result.counts.tasks}</dd></div>
                <div><dt>Entries</dt><dd className="ot-mono">{result.counts.timeEntries}</dd></div>
              </dl>
            </div>
          </div>
        </motion.div>}
      </AnimatePresence>
      <div className="ot-form__actions">
        <span className="ot-form__spacer"/>
        <Button type="button" onClick={onClose}>Cancel</Button>
        <Button type="button" tone="primary" icon="download" disabled={!result || busy} onClick={() => void confirm()}>{result ? `Import ${result.counts.timeEntries} ${result.counts.timeEntries === 1 ? "entry" : "entries"}` : "Import"}</Button>
      </div>
    </div>
  </Modal>;
}
